import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Link2, Plus, Star, Trash2, UserRound } from "lucide-react";

const EMPTY = { name: "", slot: "", qualification: "", years_of_practice: 0, note: "", photo: "", session_link: "" };

const Stars = ({ value, size = 13 }) => (
  <span className="inline-flex items-center gap-0.5 text-secondary">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} className={n <= Math.round(value) ? "fill-current" : "opacity-30"} />
    ))}
  </span>
);

const GuruForm = ({ value, specialities, onChange, onSubmit, onCancel, busy, submitLabel }) => {
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { url } = await uploadFile(file, "branding", { maxWidth: 800 });
      onChange({ photo: url });
      toast.success("Photo uploaded — press Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="guru-form">
      <div>
        <Label className="overline">Name</Label>
        <Input value={value.name} onChange={(e) => onChange({ name: e.target.value })} className="mt-2 bg-transparent" data-testid="guru-name" />
      </div>
      <div>
        <Label className="overline">Responsibility / speciality</Label>
        <Select value={value.slot} onValueChange={(v) => onChange({ slot: v })}>
          <SelectTrigger className="mt-2" data-testid="guru-slot"><SelectValue placeholder="Pick a speciality" /></SelectTrigger>
          <SelectContent>
            {specialities.map((s) => <SelectItem key={s.slot} value={s.slot}>{s.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="overline">Qualification</Label>
        <Input value={value.qualification} onChange={(e) => onChange({ qualification: e.target.value })} placeholder="BAMS, MD (Ayu), Yoga Therapist…" className="mt-2 bg-transparent" data-testid="guru-qualification" />
      </div>
      <div>
        <Label className="overline">Years of practice</Label>
        <Input type="number" min="0" value={value.years_of_practice} onChange={(e) => onChange({ years_of_practice: e.target.value })} className="mt-2 bg-transparent" data-testid="guru-years" />
      </div>
      <div className="md:col-span-2">
        <Label className="overline">Session link (Zoom / Google Meet)</Label>
        <Input value={value.session_link} onChange={(e) => onChange({ session_link: e.target.value })} placeholder="https://meet.google.com/abc-defg-hij" className="mt-2 bg-transparent" data-testid="guru-session-link" />
        <p className="text-[11px] text-muted-foreground mt-1">
          Paste this guru&apos;s own standing room link. Members who book their 7–8 PM evening see it on their dashboard.
        </p>
      </div>
      <div className="md:col-span-2">
        <Label className="overline">Note for members</Label>
        <Textarea rows={2} value={value.note} onChange={(e) => onChange({ note: e.target.value })} className="mt-2 bg-transparent" data-testid="guru-note" />
      </div>
      <div className="md:col-span-2 flex flex-wrap items-center gap-4">
        {value.photo ? <img src={mediaUrl(value.photo)} alt={value.name} className="w-14 h-14 rounded-full object-cover border border-border" /> : (
          <span className="w-14 h-14 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><UserRound size={20} /></span>
        )}
        <label className="btn-pill border border-border px-4 py-2 text-xs cursor-pointer hover:border-primary/40">
          Upload photo
          <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="guru-photo" />
        </label>
        <Button onClick={onSubmit} disabled={busy} className="btn-pill bg-primary text-primary-foreground h-10 ml-auto" data-testid="guru-save">
          {busy ? "Saving…" : submitLabel}
        </Button>
        {onCancel && <Button variant="ghost" onClick={onCancel} className="btn-pill h-10" data-testid="guru-cancel">Cancel</Button>}
      </div>
    </div>
  );
};

const GuruDetail = ({ guruId, specialities, onBack, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/yoga/admin/gurus/id/${guruId}/detail`);
    setDetail(data);
    setDraft({ ...EMPTY, ...data.guru, slot: data.responsibility.slot });
  };
  useEffect(() => { load(); }, [guruId]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/yoga/admin/gurus/id/${guruId}`, { ...draft, years_of_practice: parseInt(draft.years_of_practice, 10) || 0 });
      toast.success("Guru updated");
      await load();
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeReview = async (rid) => {
    try {
      await api.delete(`/admin/guru-reviews/${rid}`);
      toast.success("Review removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!detail) return <p className="text-sm text-muted-foreground">Loading guru…</p>;
  const a = detail.activity;

  return (
    <div className="space-y-5" data-testid="guru-detail">
      <button onClick={onBack} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1.5" data-testid="guru-detail-back">
        <ArrowLeft size={13} /> All gurus
      </button>

      <Card className="border-border">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-3xl" data-testid="guru-detail-name">{detail.guru.name}</h3>
              <div className="text-sm text-muted-foreground mt-1">
                {detail.responsibility.title}{detail.guru.qualification ? ` · ${detail.guru.qualification}` : ""}
              </div>
              <p className="text-xs text-muted-foreground mt-2 max-w-xl">{detail.responsibility.detail}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end" data-testid="guru-detail-rating">
                <Stars value={detail.rating.average} size={15} />
                <span className="font-display text-2xl">{detail.rating.average || "—"}</span>
              </div>
              <div className="text-xs text-muted-foreground">{detail.rating.count} review(s)</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6" data-testid="guru-detail-activity">
            {[
              ["Sessions", a.sessions_total],
              ["Completed", a.sessions_completed],
              ["Upcoming", a.sessions_upcoming],
              ["Cancelled", a.sessions_cancelled],
              ["Live classes", a.live_classes_assigned],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl border border-border p-3">
                <div className="font-display text-2xl">{val}</div>
                <div className="text-[11px] text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>

          {detail.guru.session_link && (
            <a href={detail.guru.session_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1.5 mt-4" data-testid="guru-detail-link">
              <Link2 size={12} /> {detail.guru.session_link}
            </a>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <h4 className="font-display text-2xl mb-4">Edit profile</h4>
          <GuruForm
            value={draft}
            specialities={specialities}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
            onSubmit={save}
            busy={busy}
            submitLabel="Save changes"
          />
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <h4 className="font-display text-2xl mb-4">Member reviews</h4>
          {detail.reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet — members can rate this guru after a completed session.</p>}
          <div className="space-y-3">
            {detail.reviews.map((r) => (
              <div key={r.id} className="border border-border rounded-xl p-4" data-testid={`guru-review-${r.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Stars value={r.rating} />
                    <span className="text-sm font-medium">{r.user_name || "Member"}</span>
                    <span className="text-xs text-muted-foreground">· session {r.session_date}</span>
                  </div>
                  <button onClick={() => removeReview(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`guru-review-delete-${r.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <h4 className="font-display text-2xl mb-4">Recent 1-on-1 sessions</h4>
          {detail.sessions.length === 0 && <p className="text-sm text-muted-foreground">No sessions assigned yet.</p>}
          <div className="space-y-2">
            {detail.sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-border pb-2" data-testid={`guru-session-${s.id}`}>
                <span>{s.date} · {s.slot}</span>
                <span className="text-muted-foreground text-xs">{s.user_name} {s.topic ? `· ${s.topic}` : ""}</span>
                <Badge variant="outline" className="text-[10px] capitalize">{s.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const GurusTab = () => {
  const [gurus, setGurus] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/yoga/admin/gurus");
    setGurus(data.gurus);
    setSpecialities(data.specialities);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/yoga/admin/gurus", { ...draft, years_of_practice: parseInt(draft.years_of_practice, 10) || 0 });
      toast.success("Guru added");
      setDraft(EMPTY);
      setAdding(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (gid) => {
    try {
      await api.delete(`/yoga/admin/gurus/id/${gid}`);
      toast.success("Guru removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (selected) {
    return (
      <GuruDetail
        guruId={selected}
        specialities={specialities}
        onBack={() => { setSelected(""); load(); }}
        onChanged={load}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="gurus-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Add as many gurus as you need — several can share a speciality. Click a guru to see their sessions, rating and reviews.
        </p>
        <Button onClick={() => setAdding((v) => !v)} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="guru-add-toggle">
          <Plus size={14} className="mr-1.5" /> Add Guru
        </Button>
      </div>

      {adding && (
        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-display text-2xl mb-4">New guru</h3>
            <GuruForm
              value={draft}
              specialities={specialities}
              onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
              onSubmit={create}
              onCancel={() => { setAdding(false); setDraft(EMPTY); }}
              busy={busy}
              submitLabel="Add guru"
            />
          </CardContent>
        </Card>
      )}

      {gurus.length === 0 && !adding && (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl" data-testid="gurus-empty">
          <UserRound className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-sm">No gurus yet — add your first one.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {gurus.map((g) => (
          <Card key={g.id} className="border-border hover:border-primary/40 transition-colors" data-testid={`guru-card-${g.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <button onClick={() => setSelected(g.id)} className="shrink-0" data-testid={`guru-open-${g.id}`}>
                {g.photo ? <img src={mediaUrl(g.photo)} alt={g.name} className="w-14 h-14 rounded-full object-cover border border-border" /> : (
                  <span className="w-14 h-14 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><UserRound size={20} /></span>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <button onClick={() => setSelected(g.id)} className="text-left" data-testid={`guru-name-${g.id}`}>
                  <div className="font-display text-xl hover:text-primary transition-colors">{g.name}</div>
                </button>
                <div className="text-xs text-muted-foreground">{g.speciality}{g.qualification ? ` · ${g.qualification}` : ""}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Stars value={g.rating.average} />
                  <span className="text-xs text-muted-foreground">{g.rating.average || "—"} ({g.rating.count})</span>
                  {g.session_link ? (
                    <Badge variant="outline" className="text-[10px] border-primary/40 text-primary bg-primary/10">Link set</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 bg-amber-500/10">No session link</Badge>
                  )}
                </div>
              </div>
              <button onClick={() => remove(g.id)} className="text-muted-foreground hover:text-destructive" data-testid={`guru-delete-${g.id}`}>
                <Trash2 size={15} />
              </button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default GurusTab;
